import { toValue, type MaybeRefOrGetter } from '@vueuse/core';

import type { TitleDetail } from '@/interfaces/title-details-graphql';
import { TitleOfferFragment } from '@/features/buybox/graphql/fragments/Offer.fragment';

import { ClassicUrl, ClickoutUrl } from '@/helpers/clickout-helper';
import { getUserLocationCountryCode } from '@/helpers/geo-location-helper';
import { Browser, useUserAgent } from '@/helpers/composables/useUserAgent';

import CountryBasedPromotionsConfig from '@/features/promotions/config.json';
import { COUNTRY_EXCEPTIONS } from '@/constants/web-locales.constant';
import { MonetizationType, ObjectType } from '@/@types/graphql-types';
import { storeToRefs } from 'pinia';
import { useRoutingStore } from '@/stores/pinia/useRoutingStore';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useTitleDetailsStore } from '@/stores/pinia/useTitleDetailsStore';
import { getCrunchyrollTargeting } from '@/helpers/ad/useCrunchyrollTargeting';
import { getBingeTargeting } from '@/helpers/ad/useBingeTargeting';
import { getTitleSqtvTargeting } from '@/helpers/ad/useTitleSqtvTargeting';
import { getOffersForCountry } from '@/helpers/offer-helper';

//////////////
//* CONFIG *//
//////////////

type MaybeRefCountryAndTitle = { country: MaybeRefOrGetter<string>; title: MaybeRefOrGetter<TitleDetail> };
type PriorityCheck = (args: MaybeRefCountryAndTitle) => 0 | 1 | 2 | 3;
type InclusionCheck = (args: MaybeRefCountryAndTitle) => boolean;

export interface CatalogueItem {
	packageId: number;
	clearName: string;
	technicalName: string;
	shortName: string;
	iconWide: string;
	offer: (
		offer: MaybeRefOrGetter<TitleOfferFragment | null>,
		country: MaybeRefOrGetter<string>,
		inCatalogue: MaybeRefOrGetter<boolean>
	) => TitleOfferFragment;
	priority: ReturnType<PriorityCheck>;
	include: ReturnType<InclusionCheck>;
	placement?: 'ft' | 'qtv' | 'sqtv';
}

/**
 * Configuration for a Provider Promotion.
 *
 * All the optional fields will be handled by `toCatalogueItem` with defaults.
 * Set them in the config to override.
 */
interface CatalogueItemConfig {
	packageId: number;
	clearName: string;
	technicalName: string;
	shortName: string;
	icon: string;
	iconWide: string;

	/** List of countries where this promotion can appear. Empty array means *every* country. */
	countries?: ReadonlyArray<string>;
	/** List of countries where the promotion should *not* appear. */
	excludeCountries?: ReadonlyArray<string>;
	/** List of countries where the promotion should *not* appear based on the visitor's geo location. */
	geoblockCountries?: ReadonlyArray<string>;

	/**
	 * If the promotion appears without a catalogue title, it will use one of
	 * the given fallback URLs. First it tries to find a country specific fallback
	 * otherwise it uses the `DEFAULT` one.
	 */
	fallbacks?: {
		DEFAULT: string;
		[countryCode: string]: string;
	};
	offer?: (
		offer: MaybeRefOrGetter<TitleOfferFragment | null>,
		country: MaybeRefOrGetter<string>
	) => TitleOfferFragment;

	/**
	 * By default, priority is decided by the order in `PromotionsConfig`.
	 * First item is highest, then second, and so on.
	 *
	 * This property lets you override the order based on the title and country.
	 */
	priority?: PriorityCheck;
	include?: InclusionCheck;

	/** Optional function to rewrite the offer URL. */
	linkRewrite?: (link: string | null | undefined, country: string) => string | null;

	placement?: 'ft' | 'qtv' | 'sqtv';
}

/** Identifies dummy offers used when there wasn't a title offer available. */
export const FALLBACK_PROMOTION_OFFER = 'PROMOTION_OFFER' as const;

// prettier-ignore
export const excludedCountriesFreeTrialATVPlus = [
	// previously geoblocked countries
	'BG', 'BM', 'BY', 'EE', 'EG', 'FJ', 'JO', 'KH', 'KN', 'KR',
	'KY', 'LA', 'LB', 'LK', 'LV', 'MD', 'MO', 'MT', 'MZ', 'NE', 'NI', 'OM', 'PA',
	'QA', 'RU', 'SK', 'SZ', 'TJ', 'TT', 'TW', 'UA', 'VN',
	// additional country exclusions from config
	'BO', 'GH', 'MU', 'PY', 'ZW',
];

export const primeImpactCountries = [
	'MX',
	'PT',
	'IE',
	'AR',
	'CR',
	'CL',
	'CO',
	'NZ',
	'PE',
	'EC',
	'GT',
	'UY',
	'PA',
	'PY',
	'BR',
	'RO',
	'TR',
	'PL',
	'DK',
	'SE',
	'BE',
	'NO',
	'FI',
	'GR',
	// GB switched to Prime Impact in March 2026 - not confirmed if it will be permanent
	'GB',
];

export const yieldOptimizationCountries = [
	'MX',
	'CL',
	'AR',
	'GB',
	'UK',
	'FR',
	'DE',
	'IT',
	'ES',
	'US',
	'BR',
	'CA',
	'AU',
];

const PROMOTION_PROVIDERS = [
	{
		/* Apple TV+ */
		packageId: 350,
		clearName: 'Apple TV',
		technicalName: 'appletvplus',
		shortName: 'atp',
		icon: '/icon/152862153/s100/appletvplus.{format}',
		iconWide: '/icon_wide/338253918/s160/appletvplus.{format}',
		fallbacks: {
			DEFAULT: 'https://tv.apple.com/channel/tvs.sbd.4000?at=1000l3V2&ct=free_trial&itscg=30200',
		},
	},
	{
		/* Apple TV - TVOD for QTV only */
		packageId: 2,
		clearName: 'Apple TV',
		technicalName: 'itunes',
		shortName: 'itu',
		icon: '/icon/190848813/s100/itunes.{format}',
		iconWide: '/icon_wide/322150876/s160/itunes.{format}',
		// prettier-ignore
		fallbacks: {
			DEFAULT: 'https://tv.apple.com/channel/tvs.sbd.4000?at=1000l3V2&ct=itunes&itscg=30200',
		},
	},
	{
		/* Dinsey Hotstar - TVOD for QTV only */
		packageId: 122,
		clearName: 'Hotstar',
		technicalName: 'hotstar',
		shortName: 'hst',
		icon: '/icon/324950024/s100/hotstar.{format}',
		iconWide: '/icon_wide/324950082/s160/hotstar.{format}',
		// prettier-ignore
		fallbacks: {
			DEFAULT: 'https://hotstar.sjv.io/jeQ9L0',
		},
	},
	{
		/* Tubi TV */
		packageId: 73,
		clearName: 'Tubi',
		technicalName: 'tubitv',
		shortName: 'tbv',
		icon: '/icon/313528601/s100/tubitv.{format}',
		iconWide: '/icon_wide/337002991/s160/tubitv.{format}',
		fallbacks: {
			DEFAULT: 'https://tubitv.com',
		},
	},
	{
		/* Viu - SVOD for QTV only */
		packageId: 158,
		clearName: 'Viu',
		technicalName: 'viu',
		shortName: 'viu',
		icon: '/icon/6918774/s100/viu.{format}',
		iconWide: '/icon_wide/322151007/s160/viu.{format}',
		// prettier-ignore
		fallbacks: {
			DEFAULT: 'https://www.viu.com/',
		},
	},
	{
		/* SkyShowtime - SVOD for QTV only */
		packageId: 1773,
		clearName: 'SkyShowtime',
		technicalName: 'skyshowtime',
		shortName: 'sst',
		icon: '/icon/330520548/s100/skyshowtime.{format}',
		iconWide: '/icon_wide/330520553/s160/skyshowtime.{format}',
		// prettier-ignore
		fallbacks: {
			DEFAULT: 'https://www.skyshowtime.com/where-can-i-stream',
		},
	},
	{
		/* Disney+ - SVOD for QTV only */
		packageId: 337,
		clearName: 'Disney+',
		technicalName: 'disneyplus',
		shortName: 'dnp',
		icon: '/icon/313118777/s100/disneyplus.{format}',
		iconWide: '/icon_wide/322151142/s160/disneyplus.{format}',
		// prettier-ignore
		fallbacks: {
			DEFAULT: 'https://www.skyshowtime.com/where-can-i-stream',
		},
	},
	{
		/* JustWatch TV - QTV only */
		packageId: 2285,
		clearName: 'JustWatchTV',
		technicalName: 'justwatchtv',
		shortName: 'jwt',
		icon: '/icon/321804885/s100/justwatchtv.{format}',
		iconWide: '/icon_wide/321804964/s160/justwatchtv.{format}',
	},
	{
		/* Amazon Prime Video */
		packageId: 9,
		clearName: 'Prime Video',
		technicalName: 'amazonprime',
		shortName: 'amp',
		icon: '/icon/52449539/s100/amazonprime.{format}',
		iconWide: '/icon_wide/322556148/s160/amazonprime.{format}',
		fallbacks: {
			DEFAULT: 'https://www.amazon.com/gp/video/offers?tag=justus1ktp-20',
			GB: 'https://www.amazon.co.uk/gp/video/offers?tag=amazon-gb-ft-21',
			DE: 'https://www.amazon.de/gp/video/primesignup?tag=amazon-de-ft-21',
			AT: 'https://www.amazon.de/gp/video/offers?tag=amazon-at-ft-21',
			US: 'https://www.amazon.com/gp/video/offers?tag=amazon-us-ft-20',
			JP: 'https://www.amazon.co.jp/gp/video/storefront?tag=amazon-jp-ft-22',
		},
		linkRewrite: amazonLinkRewrite,
	},
	{
		/* Amazon Prime Video */
		packageId: 119,
		clearName: 'Prime Video',
		technicalName: 'amazonprimevideo',
		shortName: 'prv',
		icon: '/icon/52449539/s100/amazonprime.{format}',
		iconWide: '/icon_wide/322556148/s160/amazonprimevideo.{format}',
		fallbacks: {
			DEFAULT: 'http://primevideo-eu.pxf.io/B095Wy',
			MX: 'https://primevideo-row.pxf.io/XYr3NX?subId3=amazon-mx-ft-21',
			ES: 'https://www.primevideo.com/-/es/offers?tag=amazon-es-ft-21',
			IT: 'https://www.primevideo.com/-/it/offers?tag=amazon-it-ft-21',
			FR: 'https://www.primevideo.com/-/fr/offers?tag=amazon-fr-ft-21',
			PT: 'https://primevideo-eu.pxf.io/1rKvQm?subId3=amazon-pt-ft',
			IE: 'https://primevideo-eu.pxf.io/1rKvQm?subId3=amazon-ie-ft',
			CH: 'https://www.primevideo.com/-/fr/offers?tag=amazon-ch-ft-21',
			NL: 'https://www.primevideo.com/-/nl/offers?tag=amazon-nl-ft-21',
			AU: 'https://www.primevideo.com/-/en/offers?tag=amazon-au-ft-22',
			CA: 'https://www.primevideo.com/-/en/offers?tag=amazon-ca-ft-20',
			BR: 'https://primevideo-row.pxf.io/XYr3NX?subId3=amazon-br-ft-20',
			AR: 'https://primevideo-row.pxf.io/XYr3NX?subId3=amazon-ar-ft-20',
			CR: 'https://primevideo-row.pxf.io/XYr3NX?subId3=amazon-cr-ft',
			CL: 'https://primevideo-row.pxf.io/XYr3NX?subId3=amazon-cl-ft',
			CO: 'https://primevideo-row.pxf.io/XYr3NX?subId3=amazon-co-ft',
			NZ: 'https://primevideo-row.pxf.io/XYr3NX?subId3=amazon-nz-ft',
			PE: 'https://primevideo-row.pxf.io/XYr3NX?subId3=amazon-pe-ft',
			EC: 'https://primevideo-row.pxf.io/XYr3NX?subId3=amazon-ec-ft',
			GT: 'https://primevideo-row.pxf.io/XYr3NX?subId3=amazon-gt-ft',
			UY: 'https://primevideo-row.pxf.io/XYr3NX?subId3=amazon-uy-ft',
			PA: 'https://primevideo-row.pxf.io/XYr3NX?subId3=amazon-pa-ft',
			PY: 'https://primevideo-row.pxf.io/XYr3NX?subId3=amazon-py-ft',
			RO: 'https://primevideo-eu.pxf.io/1rKvQm?subId3=amazon-ro-ft',
			TR: 'https://primevideo-eu.pxf.io/1rKvQm?subId3=amazon-tr-ft',
			PL: 'https://primevideo-eu.pxf.io/1rKvQm?subId3=amazon-pl-ft',
			DK: 'https://primevideo-eu.pxf.io/1rKvQm?subId3=amazon-dk-ft',
			SE: 'https://primevideo-eu.pxf.io/1rKvQm?subId3=amazon-se-ft',
			BE: 'https://primevideo-eu.pxf.io/1rKvQm?subId3=amazon-be-ft',
			NO: 'https://primevideo-eu.pxf.io/1rKvQm?subId3=amazon-no-ft',
			FI: 'https://primevideo-eu.pxf.io/1rKvQm?subId3=amazon-fi-ft',
			GR: 'https://primevideo-eu.pxf.io/1rKvQm?subId3=amazon-gr-ft',
			IN: 'https://www.primevideo.com/offers/nonprimehomepage/ref=atv_nb_lcl_hi_IN',
		},
		linkRewrite: amazonLinkRewrite,
	},
	{
		/* Paramount+ Amazon Channel */
		packageId: 582,
		clearName: 'Paramount+ Amazon Channel',
		technicalName: 'amazonparamountplus',
		shortName: 'app',
		icon: '/icon/246478651/s100/amazonparamountplus.{format}',
		iconWide: '/icon_wide/322151279/s160/amazonparamountplus.{format}',
		linkRewrite: amazonLinkRewrite,
	},
	{
		/* BINGE */
		packageId: 385,
		clearName: 'BINGE',
		technicalName: 'binge',
		shortName: 'bng',
		icon: '/icon/183470172/s100/binge.{format}',
		iconWide: '/icon_wide/322151825/s160/binge.{format}',
		fallbacks: {
			DEFAULT: 'https://binge.com.au/',
			AU: 'https://binge.com.au/',
		},
	},
	{
		/* Paramount Plus */
		packageId: 531,
		clearName: 'Paramount+',
		technicalName: 'paramountplus',
		shortName: 'pmp',
		icon: '/icon/242706661/s100/paramountplus.{format}',
		iconWide: '/icon_wide/322151940/s160/paramountplus.{format}',
		fallbacks: {
			DEFAULT: 'https://paramountplus.qflm.net/gb1e1g',
			US: 'https://paramountplus.qflm.net/gb1e1g',
		},
	},
	{
		/* Joyn */
		packageId: 304,
		clearName: 'Joyn',
		technicalName: 'joynde',
		shortName: 'jyn',
		icon: '/icon/305460189/s100/joynde.{format}',
		iconWide: '/icon_wide/322151187/s160/joynde.{format}',
		fallbacks: { DEFAULT: 'https://www.joyn.de/live-tv/prosieben' },
	},
	{
		/* MGM+ Amazon Channel */
		packageId: 583,
		clearName: 'MGM+ Amazon Channel',
		technicalName: 'amazonepix',
		shortName: 'aep',
		icon: '/icon/302467404/s100/amazonepix.{format}',
		iconWide: '/icon_wide/322151548/s160/amazonepix.{format}',
		fallbacks: {
			DEFAULT: 'https://www.amazon.com/gp/video/offers?benefitId=epix',
		},
	},
	{
		/* FlixOlé Amazon Channel */
		packageId: 684,
		clearName: 'FlixOlé Amazon Channel',
		technicalName: 'amazonflixole',
		shortName: 'afl',
		icon: '/icon/259252096/s100/amazonflixole.{format}',
		iconWide: '/icon_wide/322151601/s160/amazonflixole.{format}',
		linkRewrite: amazonLinkRewrite,
	},
	{
		/* Hulu */
		packageId: 15,
		clearName: 'Hulu',
		technicalName: 'hulu',
		shortName: 'hlu',
		icon: '/icon/116305230/s100/hulu.{format}',
		iconWide: '/icon_wide/322150892/s160/hulu.{format}',
		fallbacks: {
			DEFAULT: 'https://www.kmtrak.com/23H7P9/3HWS9F7/?sub1=free_trial',
		},
	},
	{
		/* Crunchyroll */
		packageId: 283,
		clearName: 'Crunchyroll',
		technicalName: 'crunchyroll',
		shortName: 'cru',
		icon: '/icon/127445869/s100/crunchyroll.{format}',
		iconWide: '/icon_wide/322150766/s160/crunchyroll.{format}',
		fallbacks: {
			DEFAULT: 'https://crunchyroll.pxf.io/xk92Nv',
		},
	},
	{
		/* Philo */
		packageId: 2383,
		clearName: 'Philo',
		technicalName: 'philo',
		shortName: 'phl',
		icon: '/icon/327419079/s100/philo.{format}',
		iconWide: '/icon_wide/327419088/s160/philo.{format}',
		fallbacks: {
			DEFAULT: 'https://philo.sjv.io/yqb1mW',
		},
	},
	{
		/* STARZ Anazon Channel */
		packageId: 1794,
		clearName: 'Starz Amazon Channel',
		technicalName: 'amazonstarz',
		shortName: 'saz',
		icon: '/icon/241732487/s100/amazonstarz.{format}',
		iconWide: '/icon_wide/322151612/s160/amazonstarz.{format}',
		fallbacks: {
			DEFAULT: 'https://www.amazon.com/gp/video/offers?benefitId=starzSub',
		},
	},
] as CatalogueItemConfig[];
// prettier-ignore
// const countriesWithATVNoSignups = [
// 			"BR", "IT", "DE", "PH", "ID", "CL", "CO", "AR", "AE",
// 			"SE", "ZA", "PL", "MY", "SG", "TH", "RU", "EG", "JP", "BG",
// 			"UA", "SK", "KR", "LK", "VN", "EE", "SI", "LV", "JO", "PY",
// 			"AZ", "MT", "MU", "OM", "GH", "AM", "MD", "TT", "KH", "VE",
// 			"UG", "NA", "BY", "BS", "AG", "CV", "GD", "BZ", "TJ", "BM",
// 			"LA", "KN", "NE"
// 		  ];

// const PrimeVideoPrioCountries = ['US', 'ES', 'BR', 'IT', 'DE', 'MX'];

/** Catalogue of Promotions, edit or add new ones here. */
// @deprecated
const PromotionsConfig: ReadonlyArray<CatalogueItemConfig> = [
	{
		/* Apple TV+ */
		packageId: 350,
		clearName: 'Apple TV',
		technicalName: 'appletvplus',
		shortName: 'atp',
		icon: '/icon/152862153/s100/appletvplus.{format}',
		iconWide: '/icon_wide/338253918/s160/appletvplus.{format}',
		// prettier-ignore
		excludeCountries: excludedCountriesFreeTrialATVPlus,
		fallbacks: {
			DEFAULT: 'https://tv.apple.com/channel/tvs.sbd.4000?at=1000l3V2&ct=free_trial&itscg=30200',
		},
		countries: [ "US", "GB", "PL"],
		include: () => true,
		priority: ({ title }) => {
			const { offers = [] } = toValue(title);
			const hasOwnOffer = offers.some(offer => offer.package.packageId === 350);

			// there are now titles with both AMZ and ATV+ offers - so this is required
			return hasOwnOffer ? 1 : 0;
		},
	},
	// {
	// 	/* Apple TV - TVOD for QTV only */
	// 	packageId: 2,
	// 	clearName: 'Apple TV',
	// 	technicalName: 'itunes',
	// 	shortName: 'itu',
	// 	icon: '/icon/190848813/s100/itunes.{format}',
	// 	iconWide: '/icon_wide/322150876/s160/itunes.{format}',
	// 	// prettier-ignore
	// 	fallbacks: {
	// 		DEFAULT: 'https://tv.apple.com/channel/tvs.sbd.4000?at=1000l3V2&ct=itunes&itscg=30200',
	// 	},
	// 	include: ({ title }) => {
	// 		const { offers = [] } = toValue(title);
	// 		const hasOwnOffer = offers.some(offer => offer.package.packageId === 2);
	// 		return !hasOwnOffer;
	// 	},
	// },
	{
		/* AppleTV+ Amazon Channel */
		packageId: 2243,
		clearName: 'AppleTV+ Amazon Channel',
		countries: ['US', 'GB', 'ES'],
		technicalName: 'amazonappletvplus',
		shortName: 'avl',
		icon: '/icon/321606410/s100/amazonappletvplus.{format}',
		iconWide: '/icon_wide/322151790/s160/amazonappletvplus.{format}',
		fallbacks: {
			DEFAULT: 'https://www.amazon.com/gp/video/offers?tag=justus1ktp-20',
			GB: 'https://www.amazon.de/gp/video/offers/?benefitId=appletvde',
			US: 'https://www.amazon.com/gp/video/offers?tag=amazon-us-ft-20',
			ES: 'https://www.primevideo.com/-/es/offers?tag=amazon-es-ft-21',
		},
	},
	{
		/* Amazon Prime Video */
		packageId: 9,
		clearName: 'Prime Video',
		technicalName: 'amazonprime',
		shortName: 'amp',
		icon: '/icon/52449539/s100/amazonprime.{format}',
		iconWide: '/icon_wide/322556148/s160/amazonprime.{format}',
		countries: [
			'GB',
			'DE',
			'US',
			'AT',
			'JP'
		],
		fallbacks: {
			DEFAULT: 'https://www.amazon.com/gp/video/offers?tag=justus1ktp-20',
			GB: 'https://www.amazon.co.uk/gp/video/offers?tag=amazon-gb-ft-21',
			DE: 'https://www.amazon.de/gp/video/primesignup?tag=amazon-de-ft-21',
			AT: 'https://www.amazon.de/gp/video/primesignup?tag=amazon-at-ft-21',
			US: 'https://www.amazon.com/gp/video/offers?tag=amazon-us-ft-20',
			JP: 'https://www.amazon.co.jp/gp/video/storefront?tag=amazon-jp-ft-22',
		},
		include() {
			return true;
		},
		priority({ country: _country }) {
			return 1;
		},
		linkRewrite: amazonLinkRewrite,
	},
	{
		/* Amazon Prime Video */
		packageId: 119,
		clearName: 'Prime Video',
		technicalName: 'amazonprimevideo',
		shortName: 'prv',
		icon: '/icon/52449539/s100/amazonprime.{format}',
		iconWide: '/icon_wide/322556148/s160/amazonprimevideo.{format}',
		countries: [
			'BR',
			'ES',
			'IT',
			'FR',
			'PT',
			'CO',
			'AR',
			'DK',
			'CR',
			'PY',
			'MX',
			'SE',
			'CL',
			'IE',
			'CH',
			'NL',
			'AU',
			'CA',
			'NZ',
			'PE',
			'EC',
			'GT',
			'UY',
			'PA',
			'PL',
			'SE',
			'BE',
			'NO',
			'GR',
			'FI',
			'RO',
			// 'TR',
			'IN'
		],
		fallbacks: {
			DEFAULT: 'http://primevideo-eu.pxf.io/B095Wy',
			MX: 'https://primevideo-row.pxf.io/XYr3NX?subId3=amazon-mx-ft-21',
			ES: 'https://www.primevideo.com/-/es/offers?tag=amazon-es-ft-21',
			IT: 'https://www.primevideo.com/-/it/offers?tag=amazon-it-ft-21',
			FR: 'https://www.primevideo.com/-/fr/offers?tag=amazon-fr-ft-21',
			PT: 'https://primevideo-eu.pxf.io/1rKvQm?subId3=amazon-pt-ft',
			IE: 'https://primevideo-eu.pxf.io/1rKvQm?subId3=amazon-ie-ft',
			CH: 'https://www.amazon.de/gp/video/primesignup?tag=amazon-ch-ft-21',
			NL: 'https://www.primevideo.com/-/nl/offers?tag=amazon-nl-ft-21',
			AU: 'https://www.primevideo.com/-/en/offers?tag=amazon-au-ft-22',
			CA: 'https://www.primevideo.com/-/en/offers?tag=amazon-ca-ft-20',
			BR: 'https://primevideo-row.pxf.io/XYr3NX?subId3=amazon-br-ft-20',
			AR: 'https://primevideo-row.pxf.io/XYr3NX?subId3=amazon-ar-ft-20',
			CR: 'https://primevideo-row.pxf.io/XYr3NX?subId3=amazon-cr-ft',
			CL: 'https://primevideo-row.pxf.io/XYr3NX?subId3=amazon-cl-ft',
			CO: 'https://primevideo-row.pxf.io/XYr3NX?subId3=amazon-co-ft',
			NZ: 'https://primevideo-row.pxf.io/XYr3NX?subId3=amazon-nz-ft',
			PE: 'https://primevideo-row.pxf.io/XYr3NX?subId3=amazon-pe-ft',
			EC: 'https://primevideo-row.pxf.io/XYr3NX?subId3=amazon-ec-ft',
			GT: 'https://primevideo-row.pxf.io/XYr3NX?subId3=amazon-gt-ft',
			UY: 'https://primevideo-row.pxf.io/XYr3NX?subId3=amazon-uy-ft',
			PA: 'https://primevideo-row.pxf.io/XYr3NX?subId3=amazon-pa-ft',
			PY: 'https://primevideo-row.pxf.io/XYr3NX?subId3=amazon-py-ft',
			RO: 'https://primevideo-eu.pxf.io/1rKvQm?subId3=amazon-ro-ft',
			TR: 'https://primevideo-eu.pxf.io/1rKvQm?subId3=amazon-tr-ft',
			PL: 'https://primevideo-eu.pxf.io/1rKvQm?subId3=amazon-pl-ft',
			DK: 'https://primevideo-eu.pxf.io/1rKvQm?subId3=amazon-dk-ft',
			SE: 'https://primevideo-eu.pxf.io/1rKvQm?subId3=amazon-se-ft',
			BE: 'https://primevideo-eu.pxf.io/1rKvQm?subId3=amazon-be-ft',
			NO: 'https://primevideo-eu.pxf.io/1rKvQm?subId3=amazon-no-ft',
			FI: 'https://primevideo-eu.pxf.io/1rKvQm?subId3=amazon-fi-ft',
			GR: 'https://primevideo-eu.pxf.io/1rKvQm?subId3=amazon-gr-ft',
			IN: 'https://www.primevideo.com/offers/nonprimehomepage/ref=atv_nb_lcl_hi_IN'
		},
		include() {
			return true;
		},
		priority() {
			return 1;
		},
		linkRewrite: amazonLinkRewrite,
	},
	{
		/* BINGE */
		packageId: 385,
		clearName: 'BINGE',
		technicalName: 'binge',
		shortName: 'bng',
		icon: '/icon/183470172/s100/binge.{format}',
		iconWide: '/icon_wide/322151825/s160/binge.{format}',
		fallbacks: {
			DEFAULT: 'https://binge.com.au/',
			AU: 'https://binge.com.au/',
		},
	},
	{
		/* MGM+ Amazon Channel */
		packageId: 583,
		clearName: 'MGM+ Amazon Channel',
		technicalName: 'amazonepix',
		shortName: 'aep',
		icon: '/icon/302467404/s100/amazonepix.{format}',
		iconWide: '/icon_wide/322151548/s160/amazonepix.{format}',
		fallbacks: {
			DEFAULT: 'https://www.amazon.com/gp/video/offers?benefitId=epix'
		}
	},
	{
		/* Paramount Plus */
		packageId: 531,
		clearName: 'Paramount+',
		technicalName: 'paramountplus',
		shortName: 'pmp',
		icon: '/icon/242706661/s100/paramountplus.{format}',
		iconWide: '/icon_wide/322151940/s160/paramountplus.{format}',
		fallbacks: {
			DEFAULT: 'https://paramountplus.qflm.net/gb1e1g',
			US: 'https://paramountplus.qflm.net/gb1e1g',
		},
	},
	// {
	// 	/* Paramount+ Amazon Channel */
	// 	packageId: 582,
	// 	clearName: 'Paramount+ Amazon Channel',
	// 	technicalName: 'amazonparamountplus',
	//  shortName: 'app',
	// 	icon: '/icon/246478651/s100/amazonparamountplus.{format}',
	// 	iconWide: '/icon_wide/322151279/s160/amazonparamountplus.{format}',
	// 	countries: ['DE', 'GB', 'IT', 'FR'],
	// 	linkRewrite: amazonLinkRewrite,
	// },
	// {
	// 	/* MGM Plus Amazon Channel */
	// 	packageId: 588,
	// 	clearName: 'MGM Plus Amazon Channel',
	// 	technicalName: 'amazonmgmplus',
	//  shortName: 'mgp',
	// 	icon: '/icon/302467404/s100/amazonepix.{format}',
	// 	iconWide: '/icon_wide/322151521/s160/amazonmgm.{format}',
	// 	countries: ['DE', 'FR', 'IT', 'GB', 'ES'],
	// 	linkRewrite: amazonLinkRewrite,
	// },
	// {
	// 	/* FlixOlé Amazon Channel */
	// 	packageId: 684,
	// 	clearName: 'FlixOlé Amazon Channel',
	// 	technicalName: 'amazonflixole',
	//  shortName: 'afl',
	// 	icon: '/icon/259252096/s100/amazonflixole.{format}',
	// 	iconWide: '/icon_wide/322151601/s160/amazonflixole.{format}',
	// 	countries: ['ES'],
	// 	linkRewrite: amazonLinkRewrite,
	// },
	{
		/* Crunchyrolll */
		packageId: 283,
		clearName: 'Crunchyroll',
		technicalName: 'crunchyroll',
	 	shortName: 'cru',
	 	icon: '/icon/127445869/s100/crunchyroll.{format}',
	 	iconWide: '/icon_wide/322150766/s160/crunchyroll.{format}',
		fallbacks: {
			DEFAULT: 'https://crunchyroll.pxf.io/xk92Nv',
		},
	},
	{
		/* Hulu */
		packageId: 15,
		clearName: 'Hulu',
		technicalName: 'hulu',
		shortName: 'hlu',
		icon: '/icon/116305230/s100/hulu.{format}',
		iconWide: '/icon_wide/322150892/s160/hulu.{format}',
		countries: ['US'],
		fallbacks: {
			DEFAULT: 'https://www.kmtrak.com/23H7P9/3HWS9F7/?sub1=free_trial',
		},
	},
	{
		/* Philo */
		packageId: 2383,
		clearName: 'Philo',
		technicalName: 'philo',
		shortName: 'phl',
		icon: '/icon/327419079/s100/philo.{format}',
		iconWide: '/icon_wide/327419088/s160/philo.{format}',
		fallbacks: {
			DEFAULT: 'https://philo.sjv.io/yqb1mW',
		},
	},
];

const titleObjectTypeMap: Record<string, ObjectType> = {
	movie: ObjectType.Movie,
	show: ObjectType.Show,
	season: ObjectType.ShowSeason,
};

// gotta make this a function instead of a constant.
// this way we can use test query params on SSR as well, not only on client.
function getYieldOptimization(title: TitleDetail) {
	const { activeRoute } = storeToRefs(useRoutingStore());
	const { webLocale } = storeToRefs(useLanguageStore());
	const { inLists } = storeToRefs(useTitleDetailsStore());
	const queryParams = activeRoute.value?.query;
	const country = (
		Object.keys(COUNTRY_EXCEPTIONS).find(key => COUNTRY_EXCEPTIONS[key] === webLocale.value) || webLocale.value
	).toUpperCase();

	// use the country_groups order to prioritise earlier groups, when a country is in multiple groups.
	// order of the rules is not determined by order of groups in admin dashboard, but order of country groups is.
	const countryGroupWeightings: Record<string, number> = {};
	(CountryBasedPromotionsConfig.config?.country_groups?.yield_optimization ?? []).forEach(
		(el, index) => (countryGroupWeightings[el.key] = index)
	);

	const countryGroups = (CountryBasedPromotionsConfig.config?.country_groups?.yield_optimization || []).reduce(
		(root, item) => {
			root[item.key] = item.countries.split(' ');
			return root;
		},
		{} as Record<string, string[]>
	);

	// this is the full interpolated yield optimisation config, that replaces country groups with each country.
	const yieldOptimizationConfig = Object.keys(CountryBasedPromotionsConfig.yield_optimization)
		// sort rules by the order of the groups. (rules do not currently retain the order from the groups)
		.slice()
		.sort((a, b) => {
			return (countryGroupWeightings[a] ?? 0) - (countryGroupWeightings[b] ?? 0);
		})
		.reduce((root, key) => {
			// check if the key is part of a country group
			if (key in countryGroups) {
				// country groups: add all countries from that country group identifier to the config
				countryGroups[key].forEach(country => {
					if (!root[country]) {
						root[country] = (CountryBasedPromotionsConfig.yield_optimization as any)[key];
					}
				});
			} else if (!root[key]) {
				// this section is for single countries only, and only if that country hasn't been set before.
				// this gives the country groups priority, if a single country is defined, but it's also part of a country group.
				root[key] = (CountryBasedPromotionsConfig.yield_optimization as any)[key];
			}
			return root;
		}, {} as Record<string, any>);

	// note: this query param hack only works when the ? query param is active.
	// when it's routed to a new page, it's not picked up anymore.
	if (queryParams?.debug_yo) {
		const [placement, providerShortName] = (queryParams.debug_yo as string).split('-');
		return {
			[country]: {
				placement,
				provider: providerShortName,
			},
		};
	} else if (!('skip_yo' in queryParams!) && yieldOptimizationConfig[country]) {
		const {
			placement: primaryPlacement,
			provider: primaryProvider,
			conditionalPlacements,
		} = yieldOptimizationConfig[country];

		let provider = getShortName(country, primaryProvider);
		let placement = primaryPlacement;
		const offers = getOffersForCountry(title.offers || [], country);

		/*
		 * Conditional Placements Logic
		 * We prioritise conditional placements over the primary placement and provider.
		 * This allows us to show a different placement/provider, when the title has an offer from that provider.
		 *
		 * Example:
		 * Primary: { placement: 'qtv', provider: 'cru' }
		 * Conditional: [ { placement: 'ft', provider: 'hlu' } ]
		 * 		Title has both hlu and cru offers => show hlu ft placement.
		 * 		Title has only cru offer => show cru qtv placement.
		 * 		Title has only hlu offer => show hlu ft placement.
		 *
		 */

		if (conditionalPlacements && Array.isArray(conditionalPlacements)) {
			// loop through each conditional placement and check if the title has an offer from that provider
			for (const conditionalPlacement of conditionalPlacements) {
				const { provider: cpProvider, placement: cpPlacement, objectType } = conditionalPlacement;
				const cpProviderShortName = getShortName(country, cpProvider);
				const hasOffer = offers.some(offer => offer.package.shortName === cpProviderShortName);
				if (objectType) {
					// objectType is optioanl parameter: movie. show, season
					if (title.objectType === titleObjectTypeMap[objectType]) {
						placement = cpPlacement;
						provider = cpProviderShortName;
					}
				} else if (hasOffer) {
					// if the title has an offer from the conditional placement provider, use that placement and provider instead
					placement = cpPlacement;
					provider = cpProviderShortName;
					// handle priority by order in array, so stop at the first match
					break;
				}
			}
		}

		if (provider === 'atp') {
			if (inLists.value.includes('YO_MANUAL_CUT_ATV')) {
				if (isValidPrimePromotionCountry(country)) {
					provider = getPrimeShortName(country);
				} else return {};
			}
		}

		/*
		 * Fronted Overrides - Crunchyroll Override
		 * For active markets from config - checks title for tags, subgenres and genres related to CR content.
		 * If present, and title is not inPromotionCatalogue set CR as provider and FT as placement.
		 */

		const crunchyrollTargeting = getCrunchyrollTargeting(title, country, provider);
		if (crunchyrollTargeting) {
			provider = crunchyrollTargeting.provider;
			placement = crunchyrollTargeting.placement;
		}

		/*
		 * Fronted Overrides - Binge Override
		 * For AU only - checks title for subgenres related to Binge content (dred, myst, thht), excluding ATV titles.
		 * If present, set BNG as provider and FT as placement.
		 */

		const bingeTargeting = getBingeTargeting(title, country);
		if (bingeTargeting) {
			provider = bingeTargeting.provider;
			placement = bingeTargeting.placement;
		}
		// Frontend Override - Title SQTV Targeting: for titles matching a config entry, force provider + sqtv placement
		const titleSqtvTargeting = getTitleSqtvTargeting(title, country);
		if (titleSqtvTargeting) {
			provider = titleSqtvTargeting.provider;
			placement = titleSqtvTargeting.placement;
		}

		/**
		 * Fronted Overrides - TVOD & SVOD Providers
		 * 		these providers cannot have an FT placement on titles with their own offers.
		 * 		FT is the current standard placement for SQTV, QTV, as these placements are only for 'watch similar titles'
		 */

		const hasOwnOffer = offers.some(offer => offer.package.shortName === provider);

		if (['itu', 'viu', 'hst', 'sst', 'dnp'].includes(provider) && hasOwnOffer) return {};

		return {
			[country]: {
				// if it has an own offer, we want the promotion clickout to forward the user to that provider + title.
				// with qtv this is not possible.
				placement: hasOwnOffer ? 'ft' : placement,
				provider,
			},
		};
	}
	return {};
}

function getShortName(country: string, provider: string) {
	if (provider === 'prime') return getPrimeShortName(country);

	return provider;
}

export function getPrimeShortName(country: string) {
	if (['US', 'GB', 'DE', 'AT', 'JP'].includes(country)) return 'amp';

	return 'prv';
}

export function getPrimeAssociatesSuffix(country: string) {
	if (['US', 'CA'].includes(country)) return '20';
	if (['AT', 'CH', 'NL'].includes(country)) return '21';
	if (['AU', 'JP'].includes(country)) return '22';

	return '';
}

function isValidPrimePromotionCountry(country: string) {
	if (primeImpactCountries.includes(country) || !!getPrimeAssociatesSuffix(country)) return true;
	return false;
}

/** Get the configured catalogue of provider promotions. */
export const getRawCatalogue = (args: MaybeRefCountryAndTitle) =>
	PromotionsConfig.map(config => toCatalogueItem(config, args))
		.filter(item => item.include)
		.sort((a, b) => b.priority - a.priority);

// exceptions:
// - if there is a placement and provider set, and the title has apple tv+ offers, then take apple tv+ as catalogue item
export const getAllRawCatalogue = (args: MaybeRefCountryAndTitle) => {
	// this is the overwritable country-based yield optimisation
	const title = toValue(args.title);

	return Object.entries(getYieldOptimization(title))
		.map(([key, value]) => {
			const item = PROMOTION_PROVIDERS.find(item => item.shortName === value.provider)!;
			const config = {
				...item,
				countries: [key],
				placement: value.placement as CatalogueItemConfig['placement'],
			};
			return {
				...toCatalogueItem(config, args),
				placement: config.placement,
			} as CatalogueItem;
		})
		.filter(Boolean)
		.filter(item => item?.include);
};

/////////////
//* UTILS *//
/////////////

/** Adds default checks and config fields to the base config defined in `PromotionsConfig`. */
export function toCatalogueItem(config: CatalogueItemConfig, args: MaybeRefCountryAndTitle): CatalogueItem {
	const { priority, packageId, clearName, technicalName, shortName, iconWide } = config;

	const country = toValue(args.country);
	const title = toValue(args.title);
	const objectType = title.objectType;

	return {
		packageId,
		clearName,
		technicalName,
		shortName,
		iconWide,

		// this offer function gets called in `usePromotion` with the latest, most relevant arguments
		offer: (
			offer: MaybeRefOrGetter<TitleOfferFragment | null>,
			country: MaybeRefOrGetter<string>,
			inCatalogue: MaybeRefOrGetter<boolean>
		) => withFallback(toValue(offer), objectType, toValue(country), toValue(inCatalogue), config),

		priority: !title || !priority ? 0 : priority.call(config, { country, title }),

		include: inclusionCheck(config, country, title),
	};
}

/** Check whether a promotion config should be included in the catalogue. */
function inclusionCheck(config: CatalogueItemConfig, country: string, title: TitleDetail) {
	const { countries = [], excludeCountries, geoblockCountries, include } = config;

	// an empty country array means "Include in Every Country"
	if (countries.length > 0 && !new Set(countries).has(country)) return false;

	if (excludeCountries && new Set(excludeCountries).has(country)) return false;

	if (geoblockCountries) {
		const geoblockedCountries = new Set(geoblockCountries);
		if (geoblockedCountries.has(country)) return false;

		const userCountry = getUserLocationCountryCode();
		if (userCountry && geoblockedCountries.has(userCountry)) return false;
	}

	if (!title) return false;

	if (include) return include.call(config, { country, title });

	return true;
}

/** Returns the title offer if there is one otherwise uses a dummy fallback with the config's URLs. */
function withFallback(
	offer: TitleOfferFragment | null,
	objectType: ObjectType,
	country: string,
	inCatalogue: boolean,
	{ clearName, packageId, icon, fallbacks, linkRewrite, iconWide, shortName, technicalName }: CatalogueItemConfig
) {
	if (offer == null) {
		const fallbackUrl = fallbacks?.[country] ?? fallbacks?.DEFAULT ?? '';

		// update fallback tags for Prime Video - first for DE and GB, then for all other countries
		const standardWebURL = ['prv', 'amp'].includes(shortName)
			? setObjectTypeOnPrimeFallbacks(objectType, fallbackUrl)
			: fallbackUrl;

		// Minimum properties needed for tracking
		return {
			id: FALLBACK_PROMOTION_OFFER,
			monetizationType: MonetizationType.Free,
			presentationType: 'hd',
			package: { clearName, packageId, iconWide, shortName, technicalName },
			standardWebURL,
		} as unknown as TitleOfferFragment;
	}

	// Original offer object is reactive, modifying it can cause infinite render loops
	const offerCopy = structuredClone(offer);

	// The actual Apple TV+ package has the clearName "Apple TV Plus"
	offerCopy.package.clearName = clearName;
	offerCopy.package.icon = icon;

	if (linkRewrite && inCatalogue) {
		offerCopy.standardWebURL = linkRewrite(offer.standardWebURL, country);
	}

	return offerCopy;
}

/////////////////////
//* LINK REWRITES *//
/////////////////////
const amazonTagByCountry: Record<string, string> = {
	DE: 'movie0c6-21',
	ES: 'just0a7-21',
	FR: 'just03b-21',
	// IT: 'justwatch07-21',
};

// const amazonPrefixByCountry: Record<string, string> = {
// 	AR: 'https://primevideo-row.pxf.io/XYr3NX',
// 	CO: 'https://primevideo-row.pxf.io/XYr3NX',
// 	DK: 'https://primevideo-eu.pxf.io/1rKvQm',
// 	PT: 'https://primevideo-eu.pxf.io/1rKvQm',
// };

export function amazonLinkRewrite(link: string | null | undefined, country: string) {
	if (!link) return null;

	const overwrittenLink = new ClassicUrl(link)
		.set('linkCode', 'xm2')
		.set('tag', amazonTagByCountry[country])
		.toString();

	// const prefix = new ClassicUrl(amazonPrefixByCountry[country]);
	// if (!prefix.error) {
	// 	return prefix.set('u', overwrittenLink).toString();
	// }

	return overwrittenLink;
}

export function setYieldOptimisationTags(clickoutURL: ClickoutUrl, providerId: number) {
	const { country } = storeToRefs(useLanguageStore());
	const { deviceOs, userBrowser } = useUserAgent();

	// Limit to select countries, except for ATV
	if (!yieldOptimizationCountries.includes(country.value) && providerId !== 350) return;

	// Only for Amazon Prime, Disney Plus, AppleTV+, Hulu and Crunchyroll
	if (![9, 119, 2100, 337, 350, 15, 283, 10, 2].includes(providerId)) return;

	if (deviceOs.value === 'macos') {
		if (userBrowser.value === 'safari') {
			// MacOS Safari
			clickoutURL.set('uct_ua', 'mac-saf');
		} else if (userBrowser.value === 'chrome') {
			// MacOS Chrome
			clickoutURL.set('uct_ua', 'mac-chr');
		} else {
			// MacOS Other
			clickoutURL.set('uct_ua', 'mac-oth');
		}
	} else if (deviceOs.value === 'ios') {
		if (userBrowser.value === 'safari') {
			// iOS Safari
			clickoutURL.set('uct_ua', 'ios-saf');
		} else if (userBrowser.value === 'chrome') {
			// iOS Chrome
			clickoutURL.set('uct_ua', 'ios-chr');
		} else {
			// iOS Other
			clickoutURL.set('uct_ua', 'ios-oth');
		}
	} else if (deviceOs.value === 'windows') {
		if (userBrowser.value === 'chrome') {
			// Windows Chrome
			clickoutURL.set('uct_ua', 'win-chr');
		} else {
			// Windows Other
			clickoutURL.set('uct_ua', 'win-oth');
		}
	} else if (deviceOs.value === 'android') {
		if (userBrowser.value === 'chrome') {
			// Android Chrome
			clickoutURL.set('uct_ua', 'and-chr');
		} else {
			// Android Other
			clickoutURL.set('uct_ua', 'and-oth');
		}
	}
}

function setObjectTypeOnPrimeFallbacks(objectType: ObjectType, fallbackUrl: string) {
	let ftStringWithObjectType = '-ft';

	if (objectType === ObjectType.Movie) {
		ftStringWithObjectType = '-ft-mov';
	} else if (objectType === ObjectType.Show) {
		ftStringWithObjectType = '-ft-sho';
	} else if (objectType === ObjectType.ShowSeason) {
		ftStringWithObjectType = '-ft-sea';
	}

	return fallbackUrl.replace('-ft', ftStringWithObjectType);
}

/*
 * Default Pacakages
 * if id is default, shortname will be derived from the yo config (as FT)
 * max. one DefaultPackage allowed, assumed to be the first package in the array
 * other packages must follow the pattern: {shortName}_{number}
 */
type DefaultPackage = { id: 'default'; shortName?: never; offerCountry?: never };
type SwiperPackage = { id: `${string}_${number}`; shortName: string; offerCountry?: string };

export interface SwiperRule {
	packages: [DefaultPackage, ...SwiperPackage[]] | SwiperPackage[];
	slideDelay: number;
	rules: {
		selectors: {
			browsers: Browser[];
			geoLocations: string[];
		};
		id: string;
	}[];
	default: {
		id: string;
	};
}

export type SwiperRulesByGroup = Record<string, { countries: string[]; swiperRule: SwiperRule }>;

// start to build a config in json format, to eventually be controlled and sent from yo dashboard
export const freeTrialSwiperRulesByGroup: SwiperRulesByGroup = {
	geoLocation_US_GB__prv: {
		countries: ['IE'],
		swiperRule: {
			packages: [
				{
					id: 'default',
				},
				{
					id: 'amp_2',
					shortName: 'amp',
					offerCountry: 'GB',
				},
			],
			slideDelay: 1500,
			rules: [
				{
					selectors: {
						browsers: [],
						geoLocations: ['GB'],
					},
					id: 'amp_2',
				},
			],
			default: {
				id: 'default',
			},
		},
	},
	geoLocation_GB__cru: {
		countries: ['IN'],
		swiperRule: {
			packages: [
				{
					id: 'default',
				},
				{
					id: 'amp_1',
					shortName: 'amp',
					offerCountry: 'GB',
				},
			],
			slideDelay: 1500,
			rules: [
				{
					selectors: {
						browsers: [],
						geoLocations: ['GB'],
					},
					id: 'amp_1',
				},
			],
			default: {
				id: 'default',
			},
		},
	},
};

export const freeTrialSwiperRules: Record<string, SwiperRule> = Object.values(freeTrialSwiperRulesByGroup).reduce(
	(root, { countries, swiperRule }) => {
		countries.forEach(country => {
			if (!root[country]) root[country] = swiperRule;
		});
		return root;
	},
	{} as Record<string, SwiperRule>
);
